import { useEffect, useState } from "react";
import { Button, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import { ApiRequest } from "GlobalFunctions";
import ClientSelector from "./components/ClientSelector";
import LineItemsTable from "./components/LineItemsTable";
import InvoiceTotals from "./components/InvoiceTotals";
import { LineItem, ClientData, mapClientToBuyer, validateInvoiceForm } from "./helpers/invoiceUtils";

export default function InvoiceCreator(props: any) {
  const { setLoading } = props;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => { setLoading(false); }, []);

  const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [paymentCode, setPaymentCode] = useState<string>("1");
  const [dueDate, setDueDate] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const errors = validateInvoiceForm(selectedClient, lineItems);
    if (errors.length > 0) {
      errors.forEach(e => enqueueSnackbar(e, { variant: "error" }));
      return;
    }

    setSubmitting(true);
    setLoading(true);

    const payload = {
      buyer: mapClientToBuyer(selectedClient!),
      lineItems: lineItems.map(item => ({
        quantity: item.quantity,
        price: item.price,
        tax_percent: item.tax_percent,
        discount_percent: item.discount_percent || 0,
        description: item.description,
        product_code: item.product_code || '',
        unit_code: item.unit_code,
      })),
      paymentMeans: { code: paymentCode, ...(paymentCode === "2" && dueDate ? { dueDate } : {}) },
      notes: notes || undefined,
    };

    await ApiRequest({
      url: "/dian/invoice/emit",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body: payload,
      setResponse: (response: any) => {
        setSubmitting(false);
        setLoading(false);
        if (response.success) {
          enqueueSnackbar("Factura emitida exitosamente", { variant: "success" });
          navigate(`/invoicing/facturas/ver/${response.document_id}`);
        } else {
          enqueueSnackbar(response.error || "Error al emitir factura", { variant: "error" });
        }
      },
    });
  };

  return (
    <div id="InvoiceCreator" className="ModuleContainer">
      <div className="ModuleWrapper">
        {/* HEADER */}
        <div className="boxContainerHeader">
          <div className="boxContainerTitle" style={{ display: "flex", alignItems: "center" }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/invoicing/facturas")}
              sx={{ mr: 2, borderRadius: 2, textTransform: "none" }}
            >
              Volver
            </Button>
            <h2 className="h2_title">Crear Factura Electronica</h2>
          </div>
        </div>

        {/* CLIENT SECTION */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", p: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: "text.secondary", textTransform: "uppercase", fontSize: 12, letterSpacing: 0.5 }}>
            Cliente
          </Typography>
          <ClientSelector selectedClient={selectedClient} onClientSelect={setSelectedClient} />
          {selectedClient && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2, backgroundColor: "#f8f9fa" }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {selectedClient.business_name || `${selectedClient.client_name} ${selectedClient.client_last_name}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedClient.document_type === "31" ? "NIT" : "CC"}: {selectedClient.document}
                {selectedClient.document_dv ? `-${selectedClient.document_dv}` : ""}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedClient.client_address}{selectedClient.city_name ? `, ${selectedClient.city_name}` : ""}
              </Typography>
              {selectedClient.client_email && (
                <Typography variant="body2" color="text.secondary">{selectedClient.client_email}</Typography>
              )}
            </Paper>
          )}
        </Paper>

        {/* LINE ITEMS */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", p: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: "text.secondary", textTransform: "uppercase", fontSize: 12, letterSpacing: 0.5 }}>
            Items
          </Typography>
          <LineItemsTable items={lineItems} onChange={setLineItems} />
          <InvoiceTotals items={lineItems} />
        </Paper>

        {/* PAYMENT & NOTES */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", p: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: "text.secondary", textTransform: "uppercase", fontSize: 12, letterSpacing: 0.5 }}>
            Pago y Notas
          </Typography>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <TextField
              select label="Medio de pago" value={paymentCode}
              onChange={(e) => setPaymentCode(e.target.value)}
              size="small"
              sx={{ minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            >
              <MenuItem value="1">Contado</MenuItem>
              <MenuItem value="2">Credito</MenuItem>
            </TextField>
            {paymentCode === "2" && (
              <TextField
                label="Fecha vencimiento" type="date" value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            )}
          </div>
          <TextField
            label="Notas (opcional)" fullWidth multiline rows={2}
            value={notes} onChange={(e) => setNotes(e.target.value)}
            size="small"
            sx={{ mt: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </Paper>

        {/* SUBMIT */}
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px 0 16px" }}>
          <Button
            variant="contained" size="large" startIcon={<SendIcon />}
            onClick={handleSubmit} disabled={submitting}
            sx={{ borderRadius: 2, textTransform: "none", px: 4 }}
          >
            Emitir Factura Electronica
          </Button>
        </div>
      </div>
    </div>
  );
}
